import _ from 'platform/utilities/data';
import { transformForSubmit } from 'platform/forms-system/src/js/helpers';
import constants from 'vets-json-schema/dist/constants.json';
import { isBDD, getBddReleaseDate } from '../utils/bdd';
import { isServingComponent } from '../utils/pageDependencies';
import { normalizeAddressLine } from '../../shared/utils/contactInformationHelpers';
import {
  NEW_CONDITION_OPTION,
  sippableId,
  form0781WorkflowChoices,
} from '../utils/v1-shared';
import {
  SHA_UPLOADS_KEY,
  VALID_ADDRESS_KEYS,
  ADDRESS_FIELDS_TO_NORMALIZE,
  WIRE_TOP_LEVEL_KEYS,
  FORM_0781_KEYS,
  BEHAVIOR_KEYS,
  UPLOAD_LOOP_KEYS,
  SERVICE_BRANCH_ENUM_BY_KEY,
  SERVICE_COMPONENT_ENUM_BY_KEY,
  ACCOUNT_TYPE_ENUM_BY_KEY,
  EVIDENCE_FACILITY_TYPES,
} from './submit-transformer-keys';
import {
  STR_ANSWER_KEY,
  STR_UPLOAD_ARRAY_PATH,
} from '../pages/supportingEvidence/serviceTreatmentRecords/options';

const SHA_VALIDATION_WARNING_CODES = new Set([
  'wrong_form',
  'unable_to_validate',
]);

export const hasShaValidationWarnings = (warnings = []) =>
  warnings.some(warning => {
    const warningCode = typeof warning === 'string' ? warning : warning?.code;
    return SHA_VALIDATION_WARNING_CODES.has(warningCode);
  });

// L023 = Other Correspondence (failed validation), L1839 = Separation Health Assessment
export const setShaAttachmentIds = formData => {
  const uploads = formData[SHA_UPLOADS_KEY];
  if (!uploads?.length) return formData;

  return _.set(
    SHA_UPLOADS_KEY,
    uploads.map(upload => {
      const { warnings, ...rest } = upload;
      if (rest.attachmentId) return rest;
      return {
        ...rest,
        attachmentId: hasShaValidationWarnings(warnings || [])
          ? 'L023'
          : 'L1839',
      };
    }),
    formData,
  );
};

// Moves SHA uploads into the attachments array and removes the original key.
export const moveShaUploadsToAttachments = formData => {
  const uploads = formData[SHA_UPLOADS_KEY];
  if (!uploads?.length) return formData;

  const clonedData = _.cloneDeep(formData);
  clonedData.attachments = [...(clonedData.attachments || []), ...uploads];
  delete clonedData[SHA_UPLOADS_KEY];
  return clonedData;
};

// Keeps SHA uploads from being stripped when a BDD claim has fallen out of the BDD window.
export const salvageOutmodedBddSha = formData => {
  const uploads = formData[SHA_UPLOADS_KEY] || [];
  const isBddShaUploaded =
    _.get('view:hasSeparationHealthAssessment', formData, false) &&
    uploads.length > 0;

  if (isBddShaUploaded && !isBDD(formData)) {
    return moveShaUploadsToAttachments(setShaAttachmentIds(formData));
  }
  return formData;
};

// Based on v1's cleanUpMailingAddress (all-claims/utils/submit.js). Also maps non-USA
// country codes to their Benefits Reference Data name (v1 does that in
// transformCountryCodeToName). Like v1, it drops unexpected keys, normalizes the address
// lines and city, omits empty values, and rewrites `TUR` based on the city.
const cleanUpMailingAddress = form => {
  const source = form?.data?.mailingAddress;
  if (!source) {
    return form;
  }

  const mailingAddress = Object.entries(source).reduce(
    (address, [key, value]) => {
      const normalizedValue = ADDRESS_FIELDS_TO_NORMALIZE.includes(key)
        ? normalizeAddressLine(value)
        : value;

      if (normalizedValue && VALID_ADDRESS_KEYS.includes(key)) {
        return { ...address, [key]: normalizedValue };
      }
      return address;
    },
    {},
  );

  if (mailingAddress.country === 'TUR') {
    mailingAddress.country =
      mailingAddress.city?.toLowerCase() === 'adana'
        ? 'Turkey (Adana only)'
        : 'Turkey (except Adana)';
  } else if (mailingAddress.country && mailingAddress.country !== 'USA') {
    // The form stores ISO codes; Lighthouse expects the Benefits Reference Data name.
    mailingAddress.country =
      constants.countries.find(c => c.value === mailingAddress.country)
        ?.label || mailingAddress.country;
  }

  return { ...form, data: { ...form.data, mailingAddress } };
};

// The form stores a root `mailingAddress` with `zipCode` and a `phoneAndEmail` card; Lighthouse
// wants them on `veteranIdentification` with split zip fields, `emailAddress` and
// `veteranPhoneNumber.telephone`.
export const reshapeVeteranIdentification = formData => {
  const { mailingAddress, phoneAndEmail, ...rest } = formData;
  if (!mailingAddress && !phoneAndEmail) return formData;

  const veteranIdentification = { ...rest.veteranIdentification };
  if (mailingAddress) {
    const { zipCode, ...address } = mailingAddress;
    const digits = (zipCode || '').replace(/\D/g, '');
    if (digits.length >= 5) address.zipFirstFive = digits.slice(0, 5);
    if (digits.length === 9) address.zipLastFour = digits.slice(5);
    veteranIdentification.mailingAddress = address;
  }
  if (phoneAndEmail?.emailAddress) {
    veteranIdentification.emailAddress = phoneAndEmail.emailAddress;
  }
  const telephone = (phoneAndEmail?.primaryPhone || '').replace(/\D/g, '');
  if (telephone) {
    veteranIdentification.veteranPhoneNumber = { telephone };
  }

  return { ...rest, veteranIdentification };
};

// Conditions

const isIncreaseRow = row =>
  !!row?.ratedDisability && row.ratedDisability !== NEW_CONDITION_OPTION;

const getConditionName = condition => {
  const name = condition?.name || condition?.condition;
  const baseName = typeof name === 'string' ? name.trim() : '';
  if (baseName && condition.sideOfBody) {
    return `${baseName}, ${condition.sideOfBody.trim().toLowerCase()}`;
  }
  return baseName;
};

/**
 * Names of every claimed condition: selected rated disabilities plus new
 * conditions (increase rows are already represented by their rated entry).
 */
export const getClaimedConditionNames = formData =>
  [
    ...(formData.ratedDisabilities || []).filter(
      rated => rated.disabilityActionType !== 'NONE',
    ),
    ...(formData.newDisabilities || []).filter(row => !isIncreaseRow(row)),
  ]
    .map(getConditionName)
    .filter(Boolean);

/**
 * Converts a sippableId-keyed checkbox map into the array of original
 * condition names that are checked and still claimed.
 */
export const transformRelatedDisabilities = (
  conditionContainer = {},
  claimedConditions = [],
) =>
  Object.keys(conditionContainer)
    .filter(key => conditionContainer[key])
    .map(key => claimedConditions.find(name => sippableId(name) === key))
    .filter(Boolean);

// Form 0781

const extractDateParts = dateString => {
  const match = dateString?.match(/^(\d{4}|\D+)-(\d{2}|\D+)/);
  return {
    treatmentMonth: match && /^\d{2}$/.test(match[2]) ? match[2] : '',
    treatmentYear: match && /^\d{4}$/.test(match[1]) ? match[1] : '',
  };
};

const joinParts = parts => parts.filter(Boolean).join(', ');

export const transformTreatmentFacilities = (
  providerFacilities = [],
  vaFacilities = [],
) => [
  ...providerFacilities
    .filter(facility => facility.treatmentLocation0781Related)
    .map(facility => ({
      facilityInfo: joinParts([
        facility.providerFacilityName,
        facility.providerFacilityAddress?.street,
        facility.providerFacilityAddress?.street2,
        facility.providerFacilityAddress?.city,
        facility.providerFacilityAddress?.state,
        facility.providerFacilityAddress?.postalCode,
        facility.providerFacilityAddress?.country,
      ]),
      ...extractDateParts(facility.treatmentDateRange?.from),
    })),
  ...vaFacilities
    .filter(facility => facility.treatmentLocation0781Related)
    .map(facility => ({
      facilityInfo: joinParts([
        facility.treatmentCenterName,
        facility.treatmentCenterAddress?.city,
        facility.treatmentCenterAddress?.state,
        facility.treatmentCenterAddress?.country,
      ]),
      ...extractDateParts(facility.treatmentDateRange?.from),
    })),
];

/**
 * The conditions page stores `treatedDisabilityNames` as a checkbox object
 * keyed by sippableId. The schema expects an array of the names of the selected
 * conditions.
 */
export const stringifyTreatedDisabilityNames = formData => {
  const facilities = formData?.vaTreatmentFacilities;
  if (!facilities?.length) {
    return formData;
  }

  const claimedConditions = getClaimedConditionNames(formData);

  return {
    ...formData,
    vaTreatmentFacilities: facilities.map(facility => ({
      ...facility,
      treatedDisabilityNames: transformRelatedDisabilities(
        facility.treatedDisabilityNames || {},
        claimedConditions,
      ),
    })),
  };
};

/**
 * Builds `form0781.treatmentProvidersDetails` from the facilities the Veteran
 * tied to a 0781 event, using the same shared transform as v1 so both forms
 * send the same shape.
 */
export const addTreatmentProvidersDetails = formData => {
  const { providerFacility, vaTreatmentFacilities } = formData || {};
  if (!providerFacility && !vaTreatmentFacilities) {
    return formData;
  }
  // if the user opted out of the 0781, don't populate `form0781.treatmentProvidersDetails`
  if (
    formData.mentalHealthWorkflowChoice ===
    form0781WorkflowChoices.OPT_OUT_OF_FORM0781
  ) {
    return formData;
  }

  return {
    ...formData,
    form0781: {
      ...formData.form0781,
      treatmentProvidersDetails: transformTreatmentFacilities(
        providerFacility || [],
        vaTreatmentFacilities || [],
      ),
    },
  };
};

// Contract helpers

const norm = str => (str || '').trim().toLowerCase();

const omitKeys = (obj, keys) =>
  Object.keys(obj).reduce(
    (acc, key) => (keys.includes(key) ? acc : { ...acc, [key]: obj[key] }),
    {},
  );

const isEmptyValue = value =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const pickKeys = (obj, keys) =>
  keys.reduce(
    (acc, key) => (isEmptyValue(obj[key]) ? acc : { ...acc, [key]: obj[key] }),
    {},
  );

// Fields the v2 schema accepts on a rated disability. Rating metadata,
// specialIssues, approximateDate, and nested secondaries are left off.
const RATED_WIRE_KEYS = [
  'name',
  'diagnosticCode',
  'ratedDisabilityId',
  'classificationCode',
];

const INCREASE_ROW_KEYS = ['ratedDisability', 'conditionDate'];

const NEW_CONDITION_WIRE_KEYS = [
  'condition',
  'cause',
  'classificationCode',
  'sideOfBody',
  'conditionDate',
  'primaryDescription',
  'causedByDisability',
  'causedByDisabilityDescription',
  'worsenedDescription',
  'worsenedEffects',
  'vaMistreatmentDescription',
  'vaMistreatmentLocation',
];

/**
 * Marks rated disabilities that the Veteran chose to increase (through an
 * increase row in `newDisabilities`) with INCREASE and all others with NONE.
 * Keeps only the rated-disability fields the v2 schema accepts and drops an
 * empty list.
 */
export const setRatedDisabilityActionTypes = formData => {
  const rated = formData.ratedDisabilities;
  if (!Array.isArray(rated) || !rated.length) {
    return omitKeys(formData, ['ratedDisabilities']);
  }

  const increases = new Map(
    (formData.newDisabilities || [])
      .filter(isIncreaseRow)
      .map(row => [norm(row.ratedDisability), row]),
  );

  return {
    ...formData,
    ratedDisabilities: rated.map(disability => {
      const increase = increases.get(norm(disability.name));
      return {
        ...pickKeys(disability, RATED_WIRE_KEYS),
        disabilityActionType: increase ? 'INCREASE' : 'NONE',
      };
    }),
  };
};

// Increase rows keep ratedDisability and conditionDate. New-condition rows
// keep the cause fields the schema lists. Drops an empty list.
export const sanitizeNewDisabilities = formData => {
  if (!Array.isArray(formData.newDisabilities)) return formData;
  const rows = formData.newDisabilities
    .filter(row => row && (isIncreaseRow(row) || (row.condition && row.cause)))
    .map(row =>
      pickKeys(
        row,
        isIncreaseRow(row) ? INCREASE_ROW_KEYS : NEW_CONDITION_WIRE_KEYS,
      ),
    );
  return rows.length
    ? { ...formData, newDisabilities: rows }
    : omitKeys(formData, ['newDisabilities']);
};

// Service information
/**
 * vets-json-schema takes `serviceBranch` and `component` as arrays of enum
 * strings (e.g. `['AIR_FORCE', 'NATIONAL_GUARD']`), but the checkbox groups store
 * `{ airForce: true }`-style objects under `serviceBranches` / `serviceComponent`.
 */
const checkedKeysToEnum = (checkboxes, enumByKey) =>
  Object.keys(checkboxes || {})
    .filter(key => checkboxes[key] === true)
    .map(key => enumByKey[key])
    .filter(Boolean);

export const transformServiceInformation = formData => {
  const { serviceInformation } = formData;
  if (!serviceInformation) return formData;
  const { serviceBranches, serviceComponent, ...rest } = serviceInformation;
  const out = { ...rest };
  if (serviceBranches) {
    out.serviceBranch = checkedKeysToEnum(
      serviceBranches,
      SERVICE_BRANCH_ENUM_BY_KEY,
    );
  }
  if (serviceComponent) {
    out.component = checkedKeysToEnum(
      serviceComponent,
      SERVICE_COMPONENT_ENUM_BY_KEY,
    );
    out.federalActivation = {
      ...rest.federalActivation,
      servedInReservesOrNationalGuard: isServingComponent(formData),
    };
  }
  return { ...formData, serviceInformation: out };
};

// Direct deposit
/**
 * bankAccountUI stores `accountType` as `checking` / `savings` and the bank name
 * as `bankName`; the schema expects `CHECKING` / `SAVINGS` and
 * `financialInstitutionName`.
 */
export const transformDirectDeposit = formData => {
  const { directDeposit } = formData;
  if (!directDeposit) return formData;
  const { accountType, bankName, ...rest } = directDeposit;
  const transformedDirectDeposit = { ...rest };
  if (accountType !== undefined) {
    transformedDirectDeposit.accountType =
      ACCOUNT_TYPE_ENUM_BY_KEY[accountType] ?? accountType;
  }
  if (bankName !== undefined) {
    transformedDirectDeposit.financialInstitutionName = bankName;
  }
  return { ...formData, directDeposit: transformedDirectDeposit };
};

// Evidence
/**
 * Drops stale `limitedConsent` text unless the Veteran answered "Yes". The
 * field stays visible and keeps its text on "No", so it must be removed here.
 * Must run before `filterEmptyObjects`, which strips `view:` fields.
 */
export const dropStaleLimitedConsent = formData => {
  if (formData['view:limitedConsent'] === true) return formData;
  return omitKeys(formData, ['limitedConsent']);
};

/**
 * Maps VA facilities to the wire shape:
 * `treatmentCenterName` -> `facilityName`,
 * `treatedDisabilityNames` -> `conditionsTreated` keyed by condition name.
 */
export const transformVaFacility = (facility, claimedConditions) => {
  const conditions = transformRelatedDisabilities(
    facility.treatedDisabilityNames,
    claimedConditions,
  );
  return {
    facilityName: facility.treatmentCenterName,
    ...(conditions.length && {
      conditionsTreated: conditions.reduce(
        (acc, name) => ({ ...acc, [name]: true }),
        {},
      ),
    }),
    ...(facility.treatmentDateRange?.from && {
      treatmentDateRange: facility.treatmentDateRange,
    }),
  };
};

export const transformVaTreatmentFacilities = formData => {
  const facilities = formData.vaTreatmentFacilities;
  if (!Array.isArray(facilities) || !facilities.length) {
    return omitKeys(formData, ['vaTreatmentFacilities']);
  }
  const claimed = getClaimedConditionNames(formData);
  return {
    ...formData,
    vaTreatmentFacilities: facilities.map(facility =>
      transformVaFacility(facility, claimed),
    ),
  };
};

/**
 * Maps a private provider facility to the 4142 wire shape: names instead of
 * sippableIds, treatmentDateRange wrapped in an array, UI-only keys removed.
 */
export const transformProviderFacility = (facility, claimedConditions) => {
  return {
    ...omitKeys(facility, ['providerType', 'providerTypeOther']),
    treatedDisabilityNames: transformRelatedDisabilities(
      facility.treatedDisabilityNames,
      claimedConditions,
    ),
    treatmentDateRange: facility.treatmentDateRange
      ? [facility.treatmentDateRange]
      : [],
  };
};

// Evidence (Lighthouse `evidence`)

// A year with an unknown month (`YYYY-XX`) is sent as `YYYY`. Lighthouse
// accepts only `YYYY-MM` today, so a year-only date depends on Lighthouse
// allowing it.
const toTreatmentDate = dateString => {
  const match = dateString?.match(/^(\d{4})-(0[1-9]|1[0-2]|XX)/);
  if (!match) return null;
  return match[2] === 'XX' ? match[1] : `${match[1]}-${match[2]}`;
};

const unique = list => [...new Set(list)];

const buildTreatment = ({ name, address, dateRange, conditions, claimed }) => {
  const dateOfTreatment = toTreatmentDate(dateRange?.from);
  const state = /^[a-zA-Z]{2}$/.test(address?.state || '')
    ? address.state
    : undefined;
  const center = Object.fromEntries(
    Object.entries({
      name,
      city: address?.city,
      state,
      country: address?.country,
    }).filter(([, value]) => value),
  );
  const treatedDisabilityName = Array.isArray(conditions)
    ? conditions.filter(condition => claimed.includes(condition))
    : transformRelatedDisabilities(conditions, claimed);

  return {
    center,
    ...(dateOfTreatment ? { dateOfTreatment } : { hasNoDate: true }),
    ...(treatedDisabilityName.length && { treatedDisabilityName }),
  };
};

/**
 * Builds the Lighthouse `evidence` object from the VA facilities and the
 * private providers. Both lists become one `treatments` array (VA first), only
 * the start date is sent (`YYYY-MM`, or `YYYY` when the month is unknown), and
 * `facilityType` is the unique set of the
 * facility types the Veteran picked, including `NOT_SURE`, which vets-api
 * removes before building the Lighthouse request. Omitted when
 * there is nothing to report. Must run before `addForm4142`, which removes
 * `providerFacility`.
 */
export const buildEvidence = formData => {
  const claimed = getClaimedConditionNames(formData);
  const vaFacilities = Array.isArray(formData.vaTreatmentFacilities)
    ? formData.vaTreatmentFacilities
    : [];
  const providers = Array.isArray(formData.providerFacility)
    ? formData.providerFacility
    : [];

  const entries = [
    ...vaFacilities.map(facility => ({
      type: facility.facilityType,
      other: facility.facilityTypeOther,
      treatment: buildTreatment({
        name: facility.treatmentCenterName,
        address: facility.treatmentCenterAddress,
        dateRange: facility.treatmentDateRange,
        conditions: facility.treatedDisabilityNames,
        claimed,
      }),
    })),
    ...providers.map(facility => ({
      type: facility.providerType,
      other: facility.providerTypeOther,
      treatment: buildTreatment({
        name: facility.providerFacilityName,
        address: facility.providerFacilityAddress,
        dateRange: facility.treatmentDateRange,
        conditions: facility.treatedDisabilityNames,
        claimed,
      }),
    })),
  ];

  const hasOther = entries.some(entry => entry.type === 'other');
  // Lighthouse requires the description to be present whenever OTHER is sent.
  const describedOther = unique(
    entries
      .filter(entry => entry.type === 'other')
      .map(entry => (entry.other || '').trim())
      .filter(Boolean),
  ).join('; ');
  const otherFacilityDescription = hasOther
    ? describedOther || 'Other'
    : undefined;

  const facilityType = unique(
    entries.map(entry => EVIDENCE_FACILITY_TYPES[entry.type]).filter(Boolean),
  );
  const treatments = entries.map(entry => entry.treatment);

  if (!treatments.length && !facilityType.length) {
    return formData;
  }

  return {
    ...formData,
    evidence: {
      ...(facilityType.length && { facilityType }),
      ...(otherFacilityDescription && { otherFacilityDescription }),
      ...(treatments.length && { treatments }),
    },
  };
};

/**
 * Builds `form4142` from the private-record facilities. Skipped when there
 * are no facilities or the Veteran has not acknowledged the authorization.
 */
export const addForm4142 = formData => {
  const { providerFacility, limitedConsent, ...rest } = formData;
  const out = omitKeys(rest, ['patient4142Acknowledgement']);

  if (
    !Array.isArray(providerFacility) ||
    !providerFacility.length ||
    formData.patient4142Acknowledgement !== true
  ) {
    return out;
  }

  const claimed = getClaimedConditionNames(formData);
  return {
    ...out,
    form4142: {
      completed2024Form: true,
      ...(limitedConsent && { limitedConsent }),
      providerFacility: providerFacility.map(facility =>
        transformProviderFacility(facility, claimed),
      ),
    },
  };
};

// Location fields only apply when a police report was filed.
export const sanitize0781PoliceReportData = formData => {
  if (!Array.isArray(formData.events)) return formData;
  return {
    ...formData,
    events: formData.events.map(event => {
      if (event.otherReports?.police === true) return event;
      return omitKeys(event, [
        'agency',
        'city',
        'country',
        'state',
        'township',
      ]);
    }),
  };
};

// Keep details only for behaviors that are still selected.
export const sanitize0781BehaviorsDetails = formData => {
  if (!formData.behaviorsDetails) return formData;
  const { workBehaviors, healthBehaviors, otherBehaviors } = formData;
  const noChange = formData.noBehavioralChange?.noChange === true;

  const behaviorsDetails = noChange
    ? {}
    : Object.keys(formData.behaviorsDetails).reduce((acc, key) => {
        const selected = [workBehaviors, healthBehaviors, otherBehaviors].some(
          group => group?.[key] === true,
        );
        return selected
          ? { ...acc, [key]: formData.behaviorsDetails[key] }
          : acc;
      }, {});
  return { ...formData, behaviorsDetails };
};

/**
 * Collects the 0781 answers into `form0781` when the Veteran chose to complete
 * the online form; otherwise discards all 0781 answers. The facility lists
 * must still be in their UI shape (they feed `treatmentProvidersDetails`).
 */
export const addForm0781 = formData => {
  const base = omitKeys(formData, [
    ...FORM_0781_KEYS,
    'noBehavioralChange',
    'answerCombatBehaviorQuestions',
    'mentalHealthWorkflowChoice',
  ]);

  if (
    formData.mentalHealthWorkflowChoice !==
    form0781WorkflowChoices.COMPLETE_ONLINE_FORM
  ) {
    return base;
  }

  let data = formData;
  if (data.answerCombatBehaviorQuestions === 'false') {
    data = omitKeys(data, BEHAVIOR_KEYS);
  }
  data = sanitize0781BehaviorsDetails(sanitize0781PoliceReportData(data));

  const form0781 = FORM_0781_KEYS.reduce(
    (acc, key) => (data[key] ? { ...acc, [key]: data[key] } : acc),
    {},
  );
  if (data.providerFacility || data.vaTreatmentFacilities) {
    form0781.treatmentProvidersDetails = transformTreatmentFacilities(
      data.providerFacility,
      data.vaTreatmentFacilities,
    );
  }

  return Object.keys(form0781).length ? { ...base, form0781 } : base;
};

// Attachments

/** Flattens uploaded loop items (`{ uploadedDocument, attachmentId }`) into attachment entries. */
const flattenUploadLoopItems = items =>
  (Array.isArray(items) ? items : [])
    .filter(
      item => item?.uploadedDocument?.confirmationCode && item?.attachmentId,
    )
    .map(({ uploadedDocument, attachmentId }) => ({
      ...uploadedDocument,
      attachmentId,
    }));

/**
 * Flattens upload loop items (`{ uploadedDocument, attachmentId }`) into
 * `attachments` entries and removes the loop arrays.
 */
export const collectUploadAttachments = formData => {
  const flattened = UPLOAD_LOOP_KEYS.flatMap(key =>
    flattenUploadLoopItems(formData[key]),
  );
  const out = omitKeys(formData, UPLOAD_LOOP_KEYS);
  if (!flattened.length) return out;
  return { ...out, attachments: [...(out.attachments || []), ...flattened] };
};

// Keeps STR uploads from being stripped when a BDD claim has fallen out of the
// BDD window (same as salvageOutmodedBddSha).
export const salvageOutmodedBddStr = formData => {
  const flattened = flattenUploadLoopItems(formData[STR_UPLOAD_ARRAY_PATH]);
  const isBddStrUploaded =
    formData[STR_ANSWER_KEY] === true && flattened.length > 0;

  if (!isBddStrUploaded || isBDD(formData)) return formData;

  const out = omitKeys(formData, [STR_UPLOAD_ARRAY_PATH]);
  return { ...out, attachments: [...(out.attachments || []), ...flattened] };
};

// Final wire filter

/** Keeps only wire keys and drops empty values. */
export const pickWireKeys = formData =>
  WIRE_TOP_LEVEL_KEYS.reduce(
    (acc, key) =>
      isEmptyValue(formData[key]) ? acc : { ...acc, [key]: formData[key] },
    {},
  );

// True when at least one submitted SHA upload passed validation, regardless of
// claim type. Uploads are only submitted when the veteran answered "yes" to
// having a SHA; otherwise they are stripped as inactive-page data.
export const hasValidatedSha = formData => {
  const uploads = formData[SHA_UPLOADS_KEY] || [];
  return (
    formData['view:hasSeparationHealthAssessment'] === true &&
    uploads.some(upload => !hasShaValidationWarnings(upload?.warnings || []))
  );
};

/**
 * Transform to the v2 submit endpoint. Starts from the platform
 * transform (drops `view:` fields and inactive-page data) and reshapes the
 * result into the `21-526EZ-ALLCLAIMS-V2` schema. Unlike the v1
 * all-claims endpoint, the v2 endpoint (`/disability_benefits/v0/submissions`)
 * validates the payload directly against that schema, so it is NOT wrapped
 * in a `{ form526 }` envelope.
 */
export function transform(formConfig, form) {
  // transformForSubmit strips non-schema fields such as upload `warnings`.
  const savedWarningsByCode = new Map(
    (form.data[SHA_UPLOADS_KEY] || [])
      .filter(upload => upload?.confirmationCode)
      .map(upload => [upload.confirmationCode, upload.warnings || []]),
  );
  const savedWarningsByIndex = (form.data[SHA_UPLOADS_KEY] || []).map(
    upload => upload?.warnings || [],
  );

  // Must be read before filterEmptyObjects strips the SHA uploads
  const hasUserUploadedSha = hasValidatedSha(form.data);

  const filterEmptyObjects = formData =>
    JSON.parse(
      // Default partial-address check requires `street`; 526 addresses use `addressLine1`.
      transformForSubmit(
        formConfig,
        cleanUpMailingAddress({ ...form, data: formData }),
        { allowPartialAddress: true },
      ),
    );

  const restoreShaWarnings = formData => {
    const uploads = formData[SHA_UPLOADS_KEY];
    if (!uploads?.length) return formData;
    return _.set(
      SHA_UPLOADS_KEY,
      uploads.map((upload, index) => ({
        ...upload,
        warnings: savedWarningsByCode.has(upload.confirmationCode)
          ? savedWarningsByCode.get(upload.confirmationCode)
          : savedWarningsByIndex[index] || [],
      })),
      formData,
    );
  };

  const addHasUserUploadedSha = formData => ({
    ...formData,
    hasUserUploadedSha,
  });

  const addBddReleaseDate = formData => {
    const releaseDate = getBddReleaseDate(form.data);
    return releaseDate
      ? _.set(
          'veteranIdentification.bddAnticipatedDateOfRelease',
          releaseDate,
          formData,
        )
      : formData;
  };

  const transformedData = [
    salvageOutmodedBddSha, // Must run before filterEmptyObjects, which strips the SHA uploads
    salvageOutmodedBddStr, // Must run before filterEmptyObjects, which strips the STR uploads
    stringifyTreatedDisabilityNames, // Must run before filterEmptyObjects
    addTreatmentProvidersDetails, // Must run before filterEmptyObjects
    dropStaleLimitedConsent, // Must run before filterEmptyObjects
    filterEmptyObjects,
    restoreShaWarnings,
    setShaAttachmentIds,
    moveShaUploadsToAttachments,
    collectUploadAttachments,
    setRatedDisabilityActionTypes, // Before the facility transforms, which read action types
    addForm0781, // Before the facility transforms; it reads the UI-shaped lists
    buildEvidence, // Before addForm4142, which removes providerFacility
    addForm4142,
    sanitizeNewDisabilities,
    addHasUserUploadedSha,
    transformServiceInformation,
    transformDirectDeposit,
    reshapeVeteranIdentification, // Before pickWireKeys, which only keeps wire keys
    addBddReleaseDate,
    pickWireKeys,
  ].reduce(
    (formData, transformer) => transformer(formData),
    _.cloneDeep(form.data),
  );

  return JSON.stringify(transformedData);
}

export default transform;
